import { addDoc, collection, doc, getDoc, getDocs, query, updateDoc } from "firebase/firestore";
import { db } from "./firebase";
import type { ContactChannel, Script } from "./types";

const scriptsCol = collection(db, "scripts_abordagem");

/** Biblioteca COMPARTILHADA entre os usuários (sem dono) — leitura/edição
 * direta é permitida (isAppUser() em firestore.rules). */
export async function fetchScripts(): Promise<Script[]> {
  const r = await getDocs(query(scriptsCol));
  return r.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<Script, "id">) }))
    .sort((a, b) => {
      if (a.ativo !== b.ativo) return a.ativo ? -1 : 1;
      const ramoA = a.ramo ?? "";
      const ramoB = b.ramo ?? "";
      if (ramoA !== ramoB) return ramoA.localeCompare(ramoB);
      return a.titulo.localeCompare(b.titulo);
    });
}

export interface ScriptInput {
  ramo: string | null;
  titulo: string;
  conteudo: string;
  canal: ContactChannel | null;
  ativo: boolean;
}

export async function createScript(input: ScriptInput): Promise<Script> {
  const agora = new Date().toISOString();
  const novo = {
    ...input,
    ramo_normalizado: input.ramo ? input.ramo.trim().toLowerCase() : null,
    criado_por: null,
    criado_em: agora,
    atualizado_em: agora,
  };
  const ref = await addDoc(scriptsCol, novo);
  return { id: ref.id, ...novo };
}

/** Sem DELETE por design (aposentar = ativo=false, preserva histórico). */
export async function updateScript(id: string, patch: Partial<ScriptInput>): Promise<Script> {
  const escrita: Record<string, unknown> = { ...patch, atualizado_em: new Date().toISOString() };
  if (patch.ramo !== undefined) escrita.ramo_normalizado = patch.ramo ? patch.ramo.trim().toLowerCase() : null;

  await updateDoc(doc(db, "scripts_abordagem", id), escrita);
  const snap = await getDoc(doc(db, "scripts_abordagem", id));
  if (!snap.exists()) throw new Error(`Script ${id} não encontrado.`);
  return { id, ...(snap.data() as Omit<Script, "id">) };
}

/** Scripts aplicáveis a um ramo, do mais específico para o mais genérico —
 * porte de scripts_para_ramo() (RPC). */
export async function scriptsParaRamo(ramo: string | null, canal?: ContactChannel | null): Promise<Script[]> {
  const todos = await fetchScripts();
  const ramoNorm = ramo ? ramo.trim().toLowerCase() : null;

  return todos
    .filter((s) => s.ativo)
    .filter((s) => s.canal === null || !canal || s.canal === canal)
    .filter((s) => s.ramo_normalizado === null || s.ramo_normalizado === ramoNorm)
    .sort((a, b) => {
      // especifico (ramo bate) antes de generico (ramo null)
      const especificoA = a.ramo_normalizado !== null ? 0 : 1;
      const especificoB = b.ramo_normalizado !== null ? 0 : 1;
      if (especificoA !== especificoB) return especificoA - especificoB;
      return a.titulo.localeCompare(b.titulo);
    });
}
